import { useState, useCallback, useEffect } from 'react';
import { dasTicketService } from '../../api/services/dasTicketService';
import type {
  ApprovalPosition,
  RelatedTicketDisplay,
  ApprovalLevelWithDynamic,
  DASTicketDetails,
  TakeActionRequest,
} from '../../api/types/dasTickets.types';
import type { User } from '../../api/types/auth.types';
import type { ID } from '../../api/types/common.types';

/**
 * Custom hook for Take Action functionality
 *
 * Handles fetching ticket data, approval position, related tickets,
 * and performing approval actions. All data comes from the backend API;
 * there is no mock/fallback data.
 */
export function useTakeAction(ticketId: ID | null) {
  // State
  const [ticket, setTicket] = useState<DASTicketDetails | null>(null);
  const [approvalPosition, setApprovalPosition] = useState<ApprovalPosition | null>(null);
  const [relatedTickets, setRelatedTickets] = useState<RelatedTicketDisplay[]>([]);
  const [approvalLevels, setApprovalLevels] = useState<ApprovalLevelWithDynamic[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch ticket data
   */
  const fetchTicketData = useCallback(async () => {
    if (!ticketId) return;

    setIsLoading(true);
    setError(null);

    try {
      // CRITICAL: Fetch ticket details (this is required)
      const ticketResponse = await dasTicketService.getDetails(ticketId);
      if (ticketResponse.success && ticketResponse.data) {
        setTicket(ticketResponse.data);
      } else {
        throw new Error(ticketResponse.message || 'Failed to fetch ticket details');
      }

      // OPTIONAL: Set default approval position
      // Note: Backend endpoint /tickets/{id}/approval-position not implemented yet
      // IMPORTANT: When a request is returned for clarification, only the creator can take action
      // Pre-approvers and other approvers should NOT be able to take action on returned requests
      const isReturned = ticketResponse.data.currentStage === 'Returned';
      const isCreator = ticketResponse.data.yourRole === 'Creator';
      const canUserTakeAction = !isReturned || isCreator;

      setApprovalPosition({
        level: 1,
        levelNumber: 1,
        levelName: 'Level 1 Approval',
        isCurrentLevel: true,
        hasActioned: false,
        canTakeAction: canUserTakeAction,
        isFinalApprover: false,
        isDynamicLevel: false,
      });

      // OPTIONAL: Set empty related tickets
      // Note: Backend endpoint /tickets/{id}/related-tickets not implemented yet
      setRelatedTickets([]);

      // OPTIONAL: Set empty approval levels
      // Note: Backend endpoint /tickets/{id}/approval-levels-with-dynamic not implemented yet
      setApprovalLevels([]);
    } catch (err) {
      const error = err as Error;
      console.error('❌ Failed to load ticket details:', {
        ticketId,
        error: error.message,
        stack: error.stack,
      });

      setError(error.message || 'Failed to load ticket details');
      setTicket(null);
    } finally {
      setIsLoading(false);
    }
  }, [ticketId]);

  /**
   * Take action on the ticket
   */
  const takeAction = useCallback(
    async (request: TakeActionRequest): Promise<void> => {
      if (!ticketId) {
        throw new Error('No ticket ID provided');
      }

      const response = await dasTicketService.takeAction(request);

      if (!response.success) {
        throw new Error(response.message || 'Failed to submit action');
      }

      // Refresh ticket data after successful action
      await fetchTicketData();
    },
    [ticketId, fetchTicketData]
  );

  /**
   * Fetch data when ticketId changes
   */
  useEffect(() => {
    if (ticketId) {
      fetchTicketData();
    }
  }, [ticketId, fetchTicketData]);

  return {
    ticket,
    approvalPosition,
    relatedTickets,
    approvalLevels,
    availableUsers,
    isLoading,
    error,
    takeAction,
    refresh: fetchTicketData,
  };
}

export default useTakeAction;
